<script setup>
  import { ExternalLink, Play, Trash2 } from "lucide-vue-next";

  import ListContainer from "@/components/episto-ui/list/ListContainer.vue";
  import ListItem from "@/components/episto-ui/list/ListItem.vue";
  import { StatusBadge } from "@/components/episto-ui/status-badge";
  import { Button } from "@/components/ui/button";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { relativeTimeFrom } from "@/utils/time";

  const props = defineProps({
    runs: { type: Array, required: true },
  });

  const emit = defineEmits(["load-run", "delete-run", "rerun"]);

  const STATUS_VARIANTS = {
    Complete: "success",
    Working: "info",
    Failed: "danger",
    Queued: "neutral",
    Cancelling: "warning",
  };


  function sheetsUrl(job) {
    const sid = job.job_attributes?.spreadsheet_id;
    if (!sid) return null;
    return `https://docs.google.com/spreadsheets/d/${sid}`;
  }
</script>

<template>
  <ListContainer
    class="!static"
    element-name="run"
    :show-header="false">
    <template #elements>
      <EmptyState
        v-if="!runs.length"
        class="px-3 py-6"
        title="No recent runs" />

      <ListItem
        v-for="(job, index) in runs"
        :key="job.id"
        :index="index"
        :element-title="job.name"
        :select-element="(idx) => emit('load-run', props.runs[idx])"
        :is-element-selected="() => false">
        <template #details>
          <StatusBadge
            :status="job.status || 'Unknown'"
            :variants="STATUS_VARIANTS" />
        </template>

        <template #actions>
          <Button
            variant="ghost"
            size="icon-sm"
            @click.stop="emit('rerun', job)">
            <Play />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            @click.stop="emit('delete-run', runs[index].id)">
            <Trash2 />
          </Button>
        </template>

        <template #additional-content>
          <div class="flex items-center justify-between w-full text-xs text-muted-foreground">
            <span>{{ relativeTimeFrom(job.created_at) }}</span>
            <a
              v-if="job.status === 'Complete' && sheetsUrl(job)"
              :href="sheetsUrl(job)"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop>
              <Button
                variant="ghost"
                size="sm"
                class="h-6 px-2 text-xs">
                <ExternalLink class="h-3 w-3 mr-1" />
                Open Sheet
              </Button>
            </a>
          </div>
        </template>
      </ListItem>
    </template>
  </ListContainer>
</template>
